import { createSlice } from "@reduxjs/toolkit";
import { dummyQueueData } from "../../constants/QueueManagementConstants";

const initialState = {
  queueList: [...dummyQueueData], // Ensure fresh array
  allQueueData: [...dummyQueueData], // Keep all data separate from filtered data
  loading: false,
  error: null,
  totalRecords: dummyQueueData.length,
};

const QueueManagementSlice = createSlice({
  name: "queueManagement",
  initialState,
  reducers: {
    addQueueEntry: (state, action) => {
      // Ensure allQueueData exists
      if (!state.allQueueData) {
        state.allQueueData = [...dummyQueueData];
      }
      
      const newEntry = {
        ...action.payload,
        id: Date.now(), 
        queueNumber: `Q${String(state.allQueueData.length + 1).padStart(3, '0')}`,
        dateCreated: new Date().toISOString().split('T')[0],
      };
      state.allQueueData.unshift(newEntry);
      state.queueList.unshift(newEntry);
      state.totalRecords = state.allQueueData.length;
    },

    // Update queue entry
    updateQueueEntry: (state, action) => {
      // Ensure allQueueData exists
      if (!state.allQueueData) {
        state.allQueueData = [...dummyQueueData];
      }
      
      const { id, ...updatedData } = action.payload;
      const allDataIndex = state.allQueueData.findIndex(entry => entry.id === id);
      const listIndex = state.queueList.findIndex(entry => entry.id === id);
      
      if (allDataIndex !== -1) {
        state.allQueueData[allDataIndex] = { ...state.allQueueData[allDataIndex], ...updatedData };
      }
      if (listIndex !== -1) {
        state.queueList[listIndex] = { ...state.queueList[listIndex], ...updatedData };
      }
    },

    // Delete queue entry
    deleteQueueEntry: (state, action) => {
      // Ensure allQueueData exists
      if (!state.allQueueData) {
        state.allQueueData = [...dummyQueueData];
      }
      
      state.allQueueData = state.allQueueData.filter(entry => entry.id !== action.payload);
      state.queueList = state.queueList.filter(entry => entry.id !== action.payload);
      state.totalRecords = state.allQueueData.length;
    },

    // Update queue status
    updateQueueStatus: (state, action) => {
      // Ensure allQueueData exists
      if (!state.allQueueData) {
        state.allQueueData = [...dummyQueueData];
      }
      
      const { id, status } = action.payload;
      const allDataIndex = state.allQueueData.findIndex(entry => entry.id === id);
      const listIndex = state.queueList.findIndex(entry => entry.id === id);
      
      if (allDataIndex !== -1) {
        state.allQueueData[allDataIndex].status = status;
      }
      if (listIndex !== -1) {
        state.queueList[listIndex].status = status;
      }
    },

    // Update waiting time
    updateWaitingTime: (state, action) => {
      // Ensure allQueueData exists
      if (!state.allQueueData) {
        state.allQueueData = [...dummyQueueData];
      }
      
      const { id, waitingTime } = action.payload;
      const allDataIndex = state.allQueueData.findIndex(entry => entry.id === id);
      const listIndex = state.queueList.findIndex(entry => entry.id === id);
      
      if (allDataIndex !== -1) {
        state.allQueueData[allDataIndex].waitingTime = waitingTime;
      }
      if (listIndex !== -1) {
        state.queueList[listIndex].waitingTime = waitingTime;
      }
    },

    // Set loading state
    setLoading: (state, action) => {
      state.loading = action.payload;
    },

    // Set error
    setError: (state, action) => {
      state.error = action.payload;
      state.loading = false;
    },

    // Clear error
    clearError: (state) => {
      state.error = null;
    },

    // Reset queue list
    resetQueueList: (state) => {
      // Ensure allQueueData exists and has data
      if (!state.allQueueData || state.allQueueData.length === 0) {
        state.allQueueData = [...dummyQueueData];
      }
      
      state.queueList = [...state.allQueueData];
      state.totalRecords = state.allQueueData.length;
      state.error = null;
      
      console.log("resetQueueList called - allQueueData:", state.allQueueData);
      console.log("resetQueueList called - queueList:", state.queueList);
    },

    // Filter by status
    filterByStatus: (state, action) => {
      const allData = state.allQueueData || dummyQueueData;
      if (action.payload === "") {
        state.queueList = [...allData];
      } else {
        state.queueList = allData.filter(entry => entry.status === action.payload);
      }
      state.totalRecords = state.queueList.length;
    },

    // Filter by department
    filterByDepartment: (state, action) => {
      const allData = state.allQueueData || dummyQueueData;
      if (action.payload === "") {
        state.queueList = [...allData];
      } else {
        state.queueList = allData.filter(entry => entry.department === action.payload);
      }
      state.totalRecords = state.queueList.length;
    },

    // Search by patient name or ID
    searchQueue: (state, action) => {
      const allData = state.allQueueData || dummyQueueData;
      const searchTerm = action.payload.toLowerCase();
      if (searchTerm === "") {
        state.queueList = [...allData];
      } else {
        state.queueList = allData.filter(entry =>
          entry.patientName.toLowerCase().includes(searchTerm) ||
          entry.patientId.toLowerCase().includes(searchTerm) ||
          entry.queueNumber.toLowerCase().includes(searchTerm)
        );
      }
      state.totalRecords = state.queueList.length;
    }
  },
});

export const {
  addQueueEntry,
  updateQueueEntry,
  deleteQueueEntry,
  updateQueueStatus,
  updateWaitingTime,
  setLoading,
  setError,
  clearError,
  resetQueueList,
  filterByStatus,
  filterByDepartment,
  searchQueue
} = QueueManagementSlice.actions;

export default QueueManagementSlice.reducer;